import { TEST_MODE, TEST_USER_ID } from '../testMode';
import { formatApiErrorDetail } from '../utils/apiErrorDetail';
import { noteBeta402 } from '../utils/betaSpendPause';
import { REQUEST_TOO_LONG, SIGN_IN_EXPIRED } from '../utils/writerCopy';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Ordinary reads (Plot Grid map, notes, balance, the urgent-message poll).
export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
// Scene Writing compose is a long database read, not a model call.
export const COMPOSE_REQUEST_TIMEOUT_MS = 90_000;
// Paid or long writes: scene generate/continue/accept, outline generate,
// chat JSON posts, and file downloads. Writer-chat streams do not use
// request(); they keep their own quiet limit.
export const LONG_REQUEST_TIMEOUT_MS = 180_000;

class ApiError extends Error {
  constructor(message, code, status, detail) {
    super(message);
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

// Token getter function - will be set by AuthenticatedApiProvider
let getAuthToken = null;

// Paths where a successful POST means "an assistant turn just completed."
// Writer chat (Discovery / Explore / Focus / WorkshopChat) can stream via
// SSE; that path dispatches the Fuel Gauge event itself when the stream
// finishes. JSON sends (Learn, Logline, mute early-return, tests) still
// complete here.
const ASSISTANT_MESSAGE_SEND_PATTERNS = [
  /^\/sessions\/[^/]+\/messages$/,
  /^\/learn\/conversations\/[^/]+\/messages$/,
  /^\/stories\/[^/]+\/log-line-workshop\/messages$/,
];

function isAssistantMessageSend(method, path) {
  return method === 'POST' && ASSISTANT_MESSAGE_SEND_PATTERNS.some((re) => re.test(path));
}

function pathWithoutQuery(path) {
  return String(path || '').split('?')[0];
}

export function timeoutForRequest(method, path, raw = false) {
  if (raw) return LONG_REQUEST_TIMEOUT_MS;
  const verb = String(method || 'GET').toUpperCase();
  const clean = pathWithoutQuery(path);
  if (verb === 'GET' && /\/scene-writing$/.test(clean)) return COMPOSE_REQUEST_TIMEOUT_MS;
  if (
    verb === 'POST'
    && (
      /\/(?:generate|continue|regenerate|redraft)$/.test(clean)
      || /\/messages$/.test(clean)
      || /\/scene-writing\/accept$/.test(clean)
      // Build with help: two model calls when the genre is first named.
      || /\/frameworks\/create\/talk$/.test(clean)
    )
  ) {
    return LONG_REQUEST_TIMEOUT_MS;
  }
  return DEFAULT_REQUEST_TIMEOUT_MS;
}

function abortError(reason) {
  if (reason?.name === 'AbortError') return reason;
  return new DOMException('The operation was aborted.', 'AbortError');
}

export function isAbortError(err) {
  return err?.name === 'AbortError';
}

/**
 * Set the token getter function for authenticated requests.
 * Called by AuthenticatedApiProvider when Clerk is ready.
 */
export function setAuthTokenGetter(getter) {
  getAuthToken = getter;
}

/**
 * Auth + JSON headers for a request. Used by the writer-chat SSE client
 * so it can fetch() with Accept: text/event-stream without going through
 * request()'s JSON parser.
 * `fresh` asks Clerk to skip its cached token (one sign-in refresh).
 */
export async function buildAuthHeaders(extra = {}, { fresh = false } = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...extra,
  };
  if (TEST_MODE) {
    headers['X-Test-User-ID'] = TEST_USER_ID;
  } else if (getAuthToken) {
    try {
      const token = await getAuthToken(fresh ? { skipCache: true } : undefined);
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('API request: No auth token available (getToken returned null/undefined)');
      }
    } catch (err) {
      console.warn('API request: Failed to get auth token:', err);
    }
  } else {
    console.warn('API request: Auth token getter not set');
  }
  return headers;
}

function isBalanceRead(path) {
  return path === '/me/openrouter/balance' || path === '/me/openrouter/balance/refresh';
}

const TOKEN_READY_POLL_MS = 200;
// A cold sign-in often has no token for a fraction of a second. The live
// check saw the token arrive 0.6s after the first empty read.
const TOKEN_READY_WAIT_MS = 1600;
const TOKEN_READY_COOLDOWN_MS = 5000;
let tokenWaitCooldownUntil = 0;

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError(signal.reason));
      return;
    }
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError(signal.reason));
    };
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Ask for a token, and if the first read is empty keep asking briefly.
 * A signed-out session stays empty; after one full wait, later calls
 * do not pause again until the cooldown passes.
 * Returns `{ token, fresh }` where fresh means a skip-cache read was used.
 */
async function waitForSignInToken(signal) {
  let token = await readToken(false, signal);
  if (token) return { token, fresh: false };
  const now = Date.now();
  if (now < tokenWaitCooldownUntil) return { token: null, fresh: true };
  // Count polls instead of wall-clock time. A test clock that only
  // advances timers would otherwise leave this loop waiting.
  const polls = Math.ceil(TOKEN_READY_WAIT_MS / TOKEN_READY_POLL_MS);
  for (let i = 0; i < polls; i += 1) {
    await delay(TOKEN_READY_POLL_MS, signal);
    token = await readToken(true, signal);
    if (token) return { token, fresh: true };
  }
  tokenWaitCooldownUntil = Date.now() + TOKEN_READY_COOLDOWN_MS;
  return { token: null, fresh: true };
}

async function readToken(skipCache, signal) {
  if (!getAuthToken) return null;
  if (signal?.aborted) throw abortError(signal.reason);
  let tokenPromise;
  try {
    tokenPromise = Promise.resolve(
      getAuthToken(skipCache ? { skipCache: true } : undefined),
    );
  } catch (err) {
    console.warn('API request: Failed to get auth token:', err);
    return null;
  }
  const token = await Promise.race([
    tokenPromise.then(
      (value) => value || null,
      (err) => {
        console.warn('API request: Failed to get auth token:', err);
        return null;
      },
    ),
    new Promise((_, reject) => {
      if (signal.aborted) {
        reject(abortError(signal.reason));
        return;
      }
      signal.addEventListener('abort', () => reject(abortError(signal.reason)), { once: true });
    }),
  ]);
  return token;
}

async function finishResponse(response, { method, path, raw }) {
  if (response.status === 401) {
    throw new ApiError(SIGN_IN_EXPIRED, 'UNAUTHORIZED', 401);
  }

  if (response.status === 402) {
    window.dispatchEvent(new Event('openrouter:balance-changed'));
  }

  if (response.status === 403) {
    throw new ApiError('Access denied', 'FORBIDDEN', 403);
  }

  if (raw) {
    if (!response.ok) {
      let message = 'Request failed';
      try {
        const data = await response.json();
        const detail = data?.error?.message ?? data?.detail;
        message = formatApiErrorDetail(detail) || message;
      } catch {
        // The body was not JSON. Keep the plain fallback.
      }
      throw new ApiError(message, 'REQUEST_FAILED', response.status);
    }
    return response;
  }

  if (response.status === 204) {
    return null;
  }

  let data;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      if (response.ok) return null;
      throw new ApiError('Invalid response from server', 'PARSE_ERROR', response.status);
    }
  }

  if (!response.ok) {
    const detail = data?.error?.message ?? data?.detail;
    const message = formatApiErrorDetail(detail) || 'Request failed';
    const apiError = new ApiError(message, data?.error?.code, response.status, data?.error || data?.detail);
    apiError.provisioned = Boolean(data?.error?.provisioned);
    if (response.status === 402) noteBeta402(data?.error);
    throw apiError;
  }

  if (isAssistantMessageSend(method, path)) {
    window.dispatchEvent(new Event('openrouter:balance-changed'));
  }

  return data;
}

async function request(path, options = {}) {
  const callerSignal = options.signal || null;
  const forceFresh = options.freshToken === true;
  const method = options.method || 'GET';
  const override = Number(options.timeoutMs);
  const timeoutMs = Number.isFinite(override) && override > 0
    ? override
    : timeoutForRequest(method, path, options.raw === true);

  const fetchOptions = { ...options };
  delete fetchOptions.freshToken;
  delete fetchOptions.timeoutMs;
  delete fetchOptions.signal;
  delete fetchOptions.raw;

  if (callerSignal?.aborted) throw abortError(callerSignal.reason);

  // One fresh-token attempt. A 401 from auth middleware means the route
  // did not run, so retrying a paid generate cannot double-charge.
  // A timeout, a 5xx, or the writer pressing Stop must not retry: the
  // server may already have started the work.
  let skipCache = forceFresh;
  let allowAuthRetry = !forceFresh && !TEST_MODE;

  while (true) {
    if (callerSignal?.aborted) throw abortError(callerSignal.reason);

    const controller = new AbortController();
    const timer = setTimeout(() => {
      if (!controller.signal.aborted) controller.abort();
    }, timeoutMs);
    const onCallerAbort = () => {
      if (!controller.signal.aborted) controller.abort(callerSignal.reason);
    };
    if (callerSignal) {
      if (callerSignal.aborted) onCallerAbort();
      else callerSignal.addEventListener('abort', onCallerAbort);
    }

    try {
      const isForm = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
      const headers = {
        ...(isForm ? {} : { 'Content-Type': 'application/json' }),
        ...fetchOptions.headers,
      };

      let token = null;
      if (TEST_MODE) {
        headers['X-Test-User-ID'] = TEST_USER_ID;
      } else if (getAuthToken) {
        if (!skipCache && allowAuthRetry) {
          const ready = await waitForSignInToken(controller.signal);
          token = ready.token;
          // Already asked for a fresh token. A 401 after that is not
          // sent again with the same empty credentials.
          if (ready.fresh) {
            skipCache = true;
            allowAuthRetry = false;
          }
        } else {
          token = await readToken(skipCache, controller.signal);
        }
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn(`API request to ${path}: No auth token available (getToken returned null/undefined)`);
        }
      } else {
        console.warn(`API request to ${path}: Auth token getter not set`);
      }

      if (!TEST_MODE && isBalanceRead(path) && !token) {
        throw new ApiError('Authentication required', 'UNAUTHORIZED', 401);
      }

      const response = await fetch(`${API_BASE}${path}`, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });

      if (response.status === 401 && allowAuthRetry) {
        allowAuthRetry = false;
        skipCache = true;
        continue;
      }

      return await finishResponse(response, { method, path, raw: options.raw === true });
    } catch (err) {
      if (err instanceof ApiError) throw err;
      if (callerSignal?.aborted) throw abortError(callerSignal.reason);
      if (controller.signal.aborted || isAbortError(err)) {
        throw new ApiError(REQUEST_TOO_LONG, 'TIMEOUT', 408);
      }
      throw err;
    } finally {
      clearTimeout(timer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    }
  }
}

export const api = {
  get: (path, options) => request(path, options),
  post: (path, body, options = {}) => request(path, {
    method: 'POST',
    body: JSON.stringify(body),
    ...(options.headers ? { headers: options.headers } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
    ...(options.freshToken ? { freshToken: true } : {}),
    ...(options.keepalive ? { keepalive: true } : {}),
    ...(Number.isFinite(Number(options.timeoutMs)) && Number(options.timeoutMs) > 0
      ? { timeoutMs: Number(options.timeoutMs) }
      : {}),
  }),
  postForm: (path, formData, options = {}) => request(path, {
    method: 'POST',
    body: formData,
    ...(options.timeoutMs ? { timeoutMs: options.timeoutMs } : {}),
  }),
  put: (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: (path, body) => request(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path) => request(path, { method: 'DELETE' }),
  /** GET returning the raw Response (for file downloads) */
  download: (path) => request(path, { raw: true })
};

export { ApiError };
